import { useContext } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, Spinner, cn } from '@design-system';
import { OnboardingFooterBar } from '@/features/client-onboarding';
import { ConsumerChromeContext } from './consumer-chrome';
import { ConsumerFooterSlot } from './consumer-footer-slot';

export interface StepAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

interface StepFooterProps {
  onBack?: () => void;
  backLabel?: string;
  primary?: StepAction;
  secondary?: StepAction;
  /** Keep the secondary action on small screens too, for steps whose two choices are both essential. */
  secondaryOnMobile?: boolean;
}

const footerButtonClass = 'rounded-full max-sm:!px-3 max-sm:text-sm';
// Guided planning's footer buttons (see `GuidedPlanningFooter`).
const guidedButtonClass = 'w-full rounded-full px-6 py-3 text-base sm:w-auto sm:px-8 sm:py-4 sm:text-lg';

/** Guided planning's Back / Next row, below the content, for a consumer handed over from it. */
function GuidedStepButtons({ onBack, backLabel = 'Back', primary, secondary }: StepFooterProps) {
  return (
    <div className="flex flex-col-reverse items-center gap-3 py-6 sm:flex-row sm:justify-center sm:gap-4 sm:py-8">
      {onBack && (
        <Button size="lg" variant="outline" className={guidedButtonClass} onClick={onBack}>
          <ChevronLeft className="size-5" />
          {backLabel}
        </Button>
      )}
      {secondary && (
        <Button
          size="lg"
          variant="outline"
          className={cn(guidedButtonClass, secondary.className)}
          onClick={secondary.onClick}
          disabled={secondary.disabled}
        >
          {secondary.label}
        </Button>
      )}
      {primary && (
        <Button
          size="lg"
          className={guidedButtonClass}
          onClick={primary.onClick}
          disabled={primary.disabled || primary.loading}
          data-testid="consumer-primary-action"
        >
          {primary.loading && <Spinner />}
          {primary.label}
          <ChevronRight className="size-5" />
        </Button>
      )}
    </div>
  );
}

/** The Back / Next bar, placed in the layout's page-wide footer when there is one. */
export function StepFooter({
  onBack,
  backLabel = 'Back',
  primary,
  secondary,
  secondaryOnMobile = false,
}: StepFooterProps) {
  const footerSlot = useContext(ConsumerFooterSlot);
  const chrome = useContext(ConsumerChromeContext);
  if (!onBack && !primary && !secondary) return null;
  if (chrome === 'guided') {
    return <GuidedStepButtons onBack={onBack} backLabel={backLabel} primary={primary} secondary={secondary} />;
  }

  const bar = (
    <OnboardingFooterBar
      back={
        onBack && (
          <Button variant="outline" size="lg" className={footerButtonClass} onClick={onBack}>
            <ChevronLeft />
            {backLabel}
          </Button>
        )
      }
      actions={
        <>
          {secondary && (
            <Button
              variant="outline"
              size="lg"
              className={cn(footerButtonClass, !secondaryOnMobile && 'max-sm:hidden', secondary.className)}
              onClick={secondary.onClick}
              disabled={secondary.disabled}
            >
              {secondary.label}
            </Button>
          )}
          {primary && (
            <Button
              size="lg"
              className={footerButtonClass}
              onClick={primary.onClick}
              disabled={primary.disabled || primary.loading}
              data-testid="consumer-primary-action"
            >
              {primary.loading && <Spinner />}
              {primary.label}
              <ChevronRight />
            </Button>
          )}
        </>
      }
    />
  );

  return footerSlot ? createPortal(bar, footerSlot) : bar;
}
